import SlideFrame from "@/components/SlideFrame";
import { useMemo, useState } from "react";
import {
  Check, ArrowRight, Calendar, Mail, Phone, Loader2, PartyPopper, Sparkles, Scale, ChevronDown,
  Building2, ShieldCheck, MessageSquareWarning, BookOpen, GraduationCap, ClipboardCheck,
  Radio, Brain, Network, FastForward,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { approvePrototype, getScope } from "@/lib/api";
import { getArchetype, getsLegalCrossSell, isBankChannel, isLawFirm, isLegalPrimary, packFor } from "@/lib/industryPack";
import { DarkeDiscountCallout, useDarkeDiscount } from "@/components/DarkeDiscount";

/* ============================ 20 · Approve Prototype ============================ */
export function S_Contact({ idx, total, prospect, goNext }) {
  const scope = getScope();
  const showLegalBanner = getsLegalCrossSell(prospect);
  const pack = packFor(prospect);
  const showCorpPricing = pack.corporate;
  const slug = (prospect?.slug || "").toLowerCase();
  const isSloan = slug === "sloan";
  const isDykema = slug === "dykema";
  const legalPrimary = isLegalPrimary(prospect);
  const [darkeActive] = useDarkeDiscount(slug);
  const darkeBumped = (isSloan || isDykema) && darkeActive;

  // Eligible archetypes for the à la carte module picker: 1A (fintech), 3 (sponsor bank), 4 (assoc), 6 (real-estate)
  const archetype = getArchetype(prospect);
  const showAlaCarte = !legalPrimary && !isLawFirm(prospect) && ["1A", "3", "4", "6"].includes(archetype);
  const ALA_CARTE_MODULES = useMemo(() => {
    const base = [
      { id: "vendor",     label: "Vendor / Third-Party Management",   sub: "KYB · OFAC · ongoing attestation",         icon: Building2 },
      { id: "marketing",  label: "Marketing & Advertising Review",    sub: "Reg-Z · UDAAP · FTC, AI pre-screen",       icon: ShieldCheck },
      { id: "complaints", label: "Complaint Management",              sub: "Intake · triage · drift detection",        icon: MessageSquareWarning },
      { id: "policies",   label: "Policies & Procedures Database",    sub: "Single source of truth · versioned",       icon: BookOpen },
      { id: "training",   label: "Training & Attestation",            sub: "Per-role tracks · auto-evidence",          icon: GraduationCap },
      { id: "risk",       label: "Risk Assessment & Audit Prep",      sub: "Continuous evidence · exam-ready packs",   icon: ClipboardCheck },
      { id: "monitoring", label: "Continuous Monitoring",             sub: "OFAC re-screen · adverse media · drift",   icon: Radio },
      { id: "complimind", label: "CompliMind — AI Q&A on your file",  sub: "Trained on your evidence · no leakage",    icon: Brain },
    ];
    if (isBankChannel(prospect)) {
      base.push({ id: "partner-monitoring", label: "Partner / Tenant Monitoring", sub: "Cross-tenant oversight for sponsor banks", icon: Network });
    }
    return base;
  }, [prospect, archetype]); // eslint-disable-line react-hooks/exhaustive-deps
  // Sensible default selection — the four most commonly requested modules
  const DEFAULT_SELECTED = ["vendor", "marketing", "training", "policies"];
  const [picked, setPicked] = useState(() => new Set(DEFAULT_SELECTED));
  const togglePick = (id) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const pickedCount = picked.size;
  const pickedLabels = ALA_CARTE_MODULES.filter((m) => picked.has(m.id)).map((m) => m.label);

  const [name, setName] = useState(prospect?.contact_name || "");
  const [email, setEmail] = useState(prospect?.contact_email || "");
  const [company, setCompany] = useState(prospect?.company_name || "");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // Legal-primary close: opt-in to also approve the platform prototype
  const [alsoWantPlatform, setAlsoWantPlatform] = useState(false);
  const CALENDLY_URL = "https://calendly.com/colindarke-complisun/complisun-legal-intro";

  const onApprove = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setBusy(true);
    try {
      // When the à la carte picker is visible, prepend selections to the notes payload
      // so Colin (and SendGrid) sees exactly which modules the prospect wants.
      let notesOut = notes.trim();
      if (showAlaCarte && pickedCount > 0) {
        const moduleList = pickedLabels.map((l) => `  • ${l}`).join("\n");
        const header = `Requested modules (${pickedCount}) — wants to skip the gap analysis for now:\n${moduleList}`;
        notesOut = notesOut ? `${header}\n\nAdditional notes:\n${notesOut}` : header;
      }
      await approvePrototype({
        name: name.trim(),
        email: email.trim(),
        company: company.trim() || null,
        notes: notesOut || null,
      });
      setDone(true);
      toast.success("Sent — Colin will reach out within 24 hours.");
    } catch (err) {
      toast.error("Couldn't send — please email colindarke@complisun.com directly.");
    } finally {
      setBusy(false);
    }
  };

  // Pricing breakdown — 20% off when approving via the deck
  const fullPlatform = 3225;
  const fullInstall = 5000;
  const platform = Math.round(fullPlatform * 0.8);   // 2,580
  const install = Math.round(fullInstall * 0.8);     // 4,000

  // Law-firm pricing (per-user model)
  const lf = pack.lawFirm;
  const lfPerUser = pack.contactPricing?.perUser || 59;
  const lfInstall = pack.contactPricing?.install || 5000;
  const lfBaseDisc = pack.contactPricing?.foundingDiscount || 0.20;
  const lfDisc = darkeBumped && lf ? 0.25 : lfBaseDisc;
  const lfDiscLabel = Math.round(lfDisc * 100);
  const lfDiscFamily = lfDisc > lfBaseDisc;
  const lfPerUserDiscounted = +(lfPerUser * (1 - lfDisc)).toFixed(2);
  const lfInstallDiscounted = Math.round(lfInstall * (1 - lfDisc));
  const lfExampleAttorneys = 25;
  const lfExampleMonthly = lfPerUser * lfExampleAttorneys;
  const lfExampleMonthlyDisc = lfPerUserDiscounted * lfExampleAttorneys;
  const lfExampleSavings = Math.round((lfExampleMonthly - lfExampleMonthlyDisc) * 12 + (lfInstall - lfInstallDiscounted));

  if (done) {
    return (
      <SlideFrame idx={idx} total={total} kicker="Locked In" dark>
        <div className="grid place-items-center min-h-[60vh] text-center">
          <div className="max-w-2xl">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-sun-500/15 border border-sun-500/40 mb-6">
              <PartyPopper className="h-7 w-7 text-sun-700" strokeWidth={1.5} />
            </div>
            <h2 className="serif font-light text-5xl md:text-6xl leading-[1.05] tracking-tight">
              {(company || name)}, <em className="italic text-sun-600">you're in.</em>
            </h2>
            <p className="mt-5 text-ink-900/75 text-lg leading-relaxed">
              Colin just got the alert. Expect an email from <strong>colindarke@complisun.com</strong> within
              24 hours to schedule the 30-minute discovery call that kicks off your 45-day prototype.
            </p>
            <div className="mt-8 inline-flex flex-wrap items-center gap-3 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/65">
              <span className="border border-ink-900/12 bg-white/60 rounded-full px-3 py-1.5">{darkeBumped ? "25% family rate" : "20% discount"} · locked</span>
              <span className="border border-sun-500/40 bg-sun-500/10 rounded-full px-3 py-1.5 text-sun-700">${platform}/mo · ${install} install</span>
            </div>
          </div>
        </div>
      </SlideFrame>
    );
  }

  return (
    <SlideFrame idx={idx} total={total} kicker={legalPrimary ? "Engage CompliSun Legal" : "Build me a Prototype"} dark>
      {/* À LA CARTE MODULE PICKER — visible for eligible non-law-firm archetypes */}
      {showAlaCarte && (
        <div className="mb-8 border border-sun-500/40 rounded-md bg-gradient-to-br from-sun-500/10 via-cream-50 to-ember-500/5 p-5 md:p-6" data-testid="ala-carte-picker">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700 flex items-center gap-1.5">
                <FastForward className="h-3 w-3" /> / skip the gap analysis for now · à la carte
              </div>
              <h3 className="serif font-light text-3xl md:text-4xl text-ink-900 mt-1.5 leading-tight">
                Pick the modules <em className="italic text-sun-600">that matter most</em> to you.
              </h3>
              <p className="mt-1.5 text-sm text-ink-900/70 max-w-2xl leading-relaxed">
                Don't want the full discovery dance? Tell us what you actually want first — tap to add or
                remove. We'll build the prototype around your picks and skip straight to "walk it".
                You can always change your mind on the call.
              </p>
            </div>
            <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.22em]">
              <span className="inline-flex items-center gap-1.5 border border-sun-500/50 bg-sun-500/15 text-sun-700 rounded-full px-3 py-1.5">
                <Check className="h-3 w-3" /> {pickedCount} module{pickedCount === 1 ? "" : "s"} picked
              </span>
              {pickedCount > 0 && (
                <button
                  type="button"
                  onClick={() => setPicked(new Set())}
                  data-testid="ala-carte-clear"
                  className="border border-ink-900/15 bg-white/60 text-ink-900/65 hover:text-ember-700 hover:border-ember-500/40 rounded-full px-3 py-1.5 transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5" data-testid="ala-carte-grid">
            {ALA_CARTE_MODULES.map((m) => {
              const on = picked.has(m.id);
              const Ic = m.icon;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => togglePick(m.id)}
                  data-testid={`ala-carte-${m.id}`}
                  aria-pressed={on}
                  className={`group relative text-left rounded-md border px-3.5 py-3 transition-all flex items-start gap-3
                    ${on
                      ? "border-sun-500 bg-white shadow-sm"
                      : "border-ink-900/12 bg-white/55 hover:border-ink-900/30 hover:bg-white/80"}`}
                >
                  <div className={`mt-0.5 h-9 w-9 rounded-md grid place-items-center flex-shrink-0 transition-colors
                    ${on ? "bg-sun-500/20 text-sun-700" : "bg-cream-100 text-ink-900/55 group-hover:text-ink-900/80"}`}>
                    <Ic className="h-4 w-4" strokeWidth={1.6} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="serif text-[15px] text-ink-900 leading-tight">{m.label}</div>
                    <div className="mono text-[9px] uppercase tracking-[0.18em] text-ink-900/55 mt-1 leading-snug">{m.sub}</div>
                  </div>
                  <div className={`flex-shrink-0 h-5 w-5 rounded-full border-2 grid place-items-center transition-colors
                    ${on ? "border-sun-500 bg-sun-500" : "border-ink-900/25 bg-white group-hover:border-ink-900/45"}`}>
                    {on && <Check className="h-3 w-3 text-ink-900" strokeWidth={3} />}
                  </div>
                </button>
              );
            })}
          </div>

          <p className="mt-4 text-[11px] text-ink-900/60 leading-relaxed">
            Selections travel with your "Build my prototype" form below — Colin sees your picks in the
            inbox alert and the discovery call starts with <em className="italic">your</em> agenda, not a generic gap analysis.
          </p>
        </div>
      )}

      <div className="grid grid-cols-12 gap-8 pt-2">
        {/* LEFT — the offer */}
        <div className="col-span-12 md:col-span-6">
          <p className="mono text-[11px] uppercase tracking-[0.22em] text-sun-700 mb-5">
            {legalPrimary ? "/ last slide · two ways to engage" : "/ last slide · let's start building"}
          </p>
          <h2 className="serif font-light text-5xl md:text-6xl leading-[1.05] tracking-tight">
            {heroTitle(legalPrimary, prospect)}
          </h2>
          <p className="mt-5 text-ink-900/75 text-lg leading-relaxed max-w-2xl">
            {heroIntro(legalPrimary, lf, lfDiscFamily, darkeBumped)}
          </p>

          {/* What you're agreeing to */}
          <div className="mt-7 border border-ink-900/12 rounded-md p-5 bg-white/70">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">/ what booking the call gets you</div>
            <ul className="mt-3 space-y-2 text-sm text-ink-900/85 leading-relaxed">
              {(legalPrimary
                ? [
                    "30-minute legal intro with Colin — we walk your top priorities, no slide deck",
                    "A tier recommendation (Hotline · Counsel · GC) + an honest answer on whether we're the right fit",
                    darkeBumped
                      ? "Pricing locks at the FAMILY rate (25% off, first 12 months) when you book"
                      : "Pricing locks at the Founding Client rate (20% off, first 12 months) when you book",
                    "Optional add-on: if your in-house GC + compliance team want the platform layer, we'll cover it on the same call",
                  ]
                : lf
                  ? [
                      "A 30-minute discovery call this week — workflow review for your firm",
                      "We build a working prototype configured to your firm, matter types, and client base",
                      "Your attorneys, paralegals, and IT walk it; we iterate until it fits",
                      lfDiscFamily
                        ? "Pricing is locked at the FAMILY rate (25% off both, first 12 months) when you approve below"
                        : "Pricing is locked at the Founding Firm rate (20% off both, first 12 months) when you approve below",
                    ]
                  : [
                      "A 30-minute discovery call this week — we walk your business plan together",
                      "We build a working prototype tailored to your products and regulators",
                      "You walk through it; we iterate until it's right",
                      "Pricing is locked at the 20% prototype-approval discount (see right) for the first 12 months",
                    ]
              ).map((t) => (
                <li key={t} className="flex items-start gap-2"><Check className="h-4 w-4 text-sun-700 mt-0.5 flex-shrink-0" /><span>{t}</span></li>
              ))}
            </ul>
          </div>

          {/* Pricing card */}
          {lf ? (
            <>
              {isDykema && (
                <div className="mt-5" data-testid="dykema-darke-wrap">
                  <DarkeDiscountCallout
                    slug={slug}
                    question="Do you know a Darke?"
                    hint="(Darke is a small club — say yes for the family rate)"
                    unlockedHeadline="Family rate unlocked"
                    unlockedSub="25% off the user fee AND the installation — locked in for the first 12 months."
                    noResponseText="No worries — Founding Firm pricing (20% off) still stands."
                    testId="dykema-darke"
                  />
                </div>
              )}
            <div className="mt-5 border border-sun-500/50 rounded-md p-5 bg-gradient-to-br from-sun-500/10 to-ember-500/5" data-testid="approve-price-card-lawfirm">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">/ {lfDiscFamily ? "family rate · darke discount" : "founding firm pricing"}</div>
                <div className={`mono text-[9px] uppercase tracking-[0.18em] ${lfDiscFamily ? "bg-sun-500 text-ink-900" : "bg-ember-500 text-cream"} rounded-full px-2 py-0.5`}>{lfDiscLabel}% · first 12 mo</div>
              </div>

              <table className="w-full text-sm">
                <thead className="text-left">
                  <tr className="border-b border-ink-900/10">
                    <th className="py-2 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 font-normal">Item</th>
                    <th className="py-2 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 font-normal text-right">Standard</th>
                    <th className="py-2 mono text-[10px] uppercase tracking-[0.22em] text-sun-700 font-normal text-right">{lfDiscFamily ? "Family rate" : "Founding firm"}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-ink-900/8">
                    <td className="py-2.5">Per user / month</td>
                    <td className="py-2.5 text-right text-ink-900/65"><span className="line-through">${lfPerUser}</span></td>
                    <td className="py-2.5 text-right"><span className="serif text-lg text-ink-900">${lfPerUserDiscounted}</span></td>
                  </tr>
                  <tr>
                    <td className="py-2.5">Installation (one-time · 45-day build)</td>
                    <td className="py-2.5 text-right text-ink-900/65"><span className="line-through">${lfInstall.toLocaleString()}</span></td>
                    <td className="py-2.5 text-right"><span className="serif text-lg text-ink-900">${lfInstallDiscounted.toLocaleString()}</span></td>
                  </tr>
                </tbody>
              </table>

              <div className="mt-4 border-t border-ink-900/10 pt-4 bg-white/40 -mx-5 -mb-5 px-5 pb-5 rounded-b-md">
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-2">/ worked example · {lfExampleAttorneys}-attorney firm</div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-ink-900/65"><span>Standard</span><span>${lfExampleMonthly.toLocaleString()}/mo + ${lfInstall.toLocaleString()} install</span></div>
                  <div className="flex justify-between"><span className="text-ink-900">{lfDiscFamily ? "Family rate" : "Founding firm"}</span><span className="serif text-base text-ink-900"><strong>${lfExampleMonthlyDisc.toLocaleString()}/mo</strong> + <strong>${lfInstallDiscounted.toLocaleString()} install</strong></span></div>
                  <div className="flex justify-between text-sun-700 mono text-[10px] uppercase tracking-[0.18em] pt-1">
                    <span>Savings · first 12 months</span><span>${lfExampleSavings.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <p className="mt-3 text-[11px] text-ink-900/60 leading-relaxed">
                {lfDiscFamily ? "Family rate" : "Founding Firm pricing"} applies to the first 12 months of the user fee and the one-time installation.
                Standard pricing applies after month 12. 3-month minimum commitment.
              </p>
            </div>
            </>
          ) : (
            <div className="mt-5 border border-sun-500/50 rounded-md p-5 bg-gradient-to-br from-sun-500/10 to-ember-500/5" data-testid="approve-price-card">
              <div className="flex items-baseline gap-3 flex-wrap">
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">Locked in when you approve below</div>
              </div>
              <div className="mt-2 flex items-baseline flex-wrap gap-4">
                <div className="serif text-4xl md:text-5xl text-ink-900">${platform.toLocaleString()}<span className="text-base font-normal text-ink-900/65">/mo</span></div>
                <div className="text-sm text-ink-900/60">
                  <span className="line-through opacity-60">${fullPlatform.toLocaleString()}</span> · <span className="mono text-sun-700">20% off</span>
                </div>
              </div>
              <div className="mt-1 flex items-baseline flex-wrap gap-4">
                <div className="serif text-xl text-ink-900">+ ${install.toLocaleString()} installation</div>
                <div className="text-sm text-ink-900/60">
                  <span className="line-through opacity-60">${fullInstall.toLocaleString()}</span> · <span className="mono text-sun-700">covers the 45-day rollout</span>
                </div>
              </div>
              <div className="mt-2 text-xs text-ink-900/65">
                Includes 20 hrs/mo fractional compliance team · $200/hr beyond that.
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — the green-light form (or legal-primary Calendly hero) */}
        <div className="col-span-12 md:col-span-6">
          {legalPrimary ? (
            <div className="space-y-4">
              {/* Primary CTA — schedule legal consultation */}
              <div className="border border-sun-500/60 rounded-md p-6 md:p-7 bg-gradient-to-br from-sun-500/12 via-ember-500/8 to-sun-500/12 shadow-sm" data-testid="legal-primary-cta">
                <div className="flex items-center gap-2 mb-1">
                  <Scale className="h-4 w-4 text-sun-700" strokeWidth={1.6} />
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">/ primary · outside counsel</div>
                </div>
                <div className="serif text-2xl text-ink-900 leading-tight">Schedule the legal intro.</div>
                <p className="mt-1.5 text-sm text-ink-900/65 leading-relaxed">
                  30 minutes with Colin. Walk your priorities. Pick a tier. We can also cover the platform on the same call if you want it.
                </p>

                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="legal-primary-schedule"
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 h-14 rounded-md bg-ink-900 hover:bg-ink-800 text-cream serif text-lg group transition-colors"
                >
                  Schedule legal consultation
                  <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                </a>

                <div className="mt-4 flex flex-wrap gap-2 mono text-[9px] uppercase tracking-[0.20em] text-ink-900/55">
                  <span className="border border-sun-500/40 bg-white/55 rounded-full px-2.5 py-1">
                    {darkeBumped ? "25% family rate · locked at booking" : "20% Founding · locked at booking"}
                  </span>
                  <span className="border border-ink-900/12 bg-white/55 rounded-full px-2.5 py-1">3-month minimum · month-to-month after</span>
                </div>

                <p className="mt-3 text-[11px] text-ink-900/55 leading-relaxed text-center">
                  Booking opens Colin's calendar in a new tab — no form to fill, no commitment beyond the 30-minute intro.
                </p>
              </div>

              {/* Secondary opt-in — platform prototype */}
              <button
                type="button"
                onClick={() => setAlsoWantPlatform((s) => !s)}
                data-testid="legal-primary-toggle-platform"
                className="w-full text-left border border-ink-900/12 rounded-md p-4 bg-white/55 hover:border-ink-900/25 transition-colors group"
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 h-4 w-4 flex-shrink-0 rounded border-2 grid place-items-center transition-colors
                    ${alsoWantPlatform ? "border-sun-500 bg-sun-500" : "border-ink-900/30 bg-white"}`}>
                    {alsoWantPlatform && <Check className="h-3 w-3 text-ink-900" strokeWidth={3} />}
                  </div>
                  <div className="flex-1">
                    <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 group-hover:text-sun-700 transition-colors">/ optional · secondary</div>
                    <div className="serif text-base text-ink-900 leading-tight mt-0.5">Also approve the platform prototype for our in-house team</div>
                    <p className="text-xs text-ink-900/65 mt-1 leading-relaxed">
                      Add the tech layer your GC and compliance team use day-to-day — matter tracker, CompliMind,
                      hour markers. We'll prep both on the discovery call.
                    </p>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-ink-900/40 transition-transform ${alsoWantPlatform ? "rotate-180" : ""}`} />
                </div>
              </button>

              {alsoWantPlatform && (
                <form onSubmit={onApprove} className="border border-ink-900/12 rounded-md p-5 bg-white/80 space-y-3" data-testid="approve-form">
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">/ platform prototype · pre-approval</div>
                  <p className="text-xs text-ink-900/65 leading-relaxed">
                    Quick info so we can prep both the legal scope and the platform prototype before the call.
                  </p>
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-1.5 block">Your name *</label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name"
                           data-testid="approve-name"
                           className="h-10 bg-white border-ink-900/15 focus-visible:ring-sun-500 focus-visible:border-sun-500" />
                  </div>
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-1.5 block">Work email *</label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                           data-testid="approve-email"
                           className="h-10 bg-white border-ink-900/15 focus-visible:ring-sun-500 focus-visible:border-sun-500" />
                  </div>
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-1.5 block">Company</label>
                    <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company"
                           className="h-10 bg-white border-ink-900/15 focus-visible:ring-sun-500 focus-visible:border-sun-500" />
                  </div>
                  <Button type="submit" disabled={busy || !name.trim() || !email.trim()}
                          data-testid="approve-submit"
                          className="w-full h-11 rounded-md bg-ember-600 hover:bg-ember-700 disabled:opacity-40 text-cream mono text-[11px] uppercase tracking-[0.22em] group">
                    {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> Sending…</>
                          : <>Pre-approve platform prototype <ArrowRight className="h-3.5 w-3.5 ml-2 group-hover:translate-x-0.5 transition-transform" /></>}
                  </Button>
                </form>
              )}

              {/* Alt contact paths */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <a href="mailto:colindarke@complisun.com" className="border border-ink-900/10 rounded-md p-3 bg-white/55 hover:bg-white transition-colors group" data-testid="approve-email-link">
                  <Mail className="h-4 w-4 text-ink-900/55 mx-auto mb-1.5 group-hover:text-sun-700" strokeWidth={1.5} />
                  <div className="mono text-[9px] uppercase tracking-[0.18em] text-ink-900/55 group-hover:text-sun-700">Email</div>
                  <div className="text-[11px] text-ink-900/65 leading-tight truncate">colindarke@complisun.com</div>
                </a>
                <a href="tel:+13136174473" className="border border-ink-900/10 rounded-md p-3 bg-white/55 hover:bg-white transition-colors group">
                  <Phone className="h-4 w-4 text-ink-900/55 mx-auto mb-1.5 group-hover:text-sun-700" strokeWidth={1.5} />
                  <div className="mono text-[9px] uppercase tracking-[0.18em] text-ink-900/55 group-hover:text-sun-700">Call</div>
                  <div className="text-[11px] text-ink-900/65 leading-tight">313 · 617 · 4473</div>
                </a>
                <a href="https://complisunguardian.com" target="_blank" rel="noreferrer" className="border border-ink-900/10 rounded-md p-3 bg-white/55 hover:bg-white transition-colors group">
                  <Calendar className="h-4 w-4 text-ink-900/55 mx-auto mb-1.5 group-hover:text-sun-700" strokeWidth={1.5} />
                  <div className="mono text-[9px] uppercase tracking-[0.18em] text-ink-900/55 group-hover:text-sun-700">Try free</div>
                  <div className="text-[11px] text-ink-900/65 leading-tight">60-day trial</div>
                </a>
              </div>
            </div>
          ) : (
          <form onSubmit={onApprove} className="border border-ink-900/12 rounded-md p-6 md:p-7 bg-white/80 shadow-sm" data-testid="approve-form">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-4 w-4 text-sun-700" strokeWidth={1.5} />
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">/ approve · {darkeBumped ? "25% family rate" : "20% locked in"}</div>
            </div>
            <div className="serif text-2xl text-ink-900 leading-tight">Build my prototype.</div>
            <p className="mt-1.5 text-sm text-ink-900/65">
              {showAlaCarte && pickedCount > 0
                ? <>We'll build around your <strong className="text-ink-900">{pickedCount} picked module{pickedCount === 1 ? "" : "s"}</strong> and email you within 24 hours.</>
                : <>We'll email you within 24 hours to schedule the discovery call.</>}
            </p>

            {showAlaCarte && pickedCount > 0 && (
              <div className="mt-3 border border-sun-500/30 bg-sun-500/10 rounded-md p-3" data-testid="approve-picked-summary">
                <div className="mono text-[9px] uppercase tracking-[0.22em] text-sun-700 mb-1.5">/ your picks travel with this form</div>
                <div className="flex flex-wrap gap-1.5">
                  {pickedLabels.map((l) => (
                    <span key={l} className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-[0.18em] bg-white border border-sun-500/30 text-ink-900/80 rounded-full px-2 py-0.5">
                      <Check className="h-2.5 w-2.5 text-sun-700" /> {l}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-5 space-y-3">
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-1.5 block">Your name *</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name"
                       data-testid="approve-name"
                       className="h-11 bg-white border-ink-900/15 focus-visible:ring-sun-500 focus-visible:border-sun-500" />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-1.5 block">Work email *</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                       data-testid="approve-email"
                       className="h-11 bg-white border-ink-900/15 focus-visible:ring-sun-500 focus-visible:border-sun-500" />
              </div>
              {!prospect && (
                <div>
                  <label className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-1.5 block">Company</label>
                  <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company name"
                         data-testid="approve-company"
                         className="h-11 bg-white border-ink-900/15 focus-visible:ring-sun-500 focus-visible:border-sun-500" />
                </div>
              )}
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-1.5 block">Anything Colin should prep before the call?</label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                          placeholder="e.g. We're a non-bank lender expanding into 6 new states; ramp by Q3."
                          rows={3}
                          data-testid="approve-notes"
                          className="bg-white border-ink-900/15 focus-visible:ring-sun-500 focus-visible:border-sun-500" />
              </div>
            </div>

            <Button type="submit" disabled={busy || !name.trim() || !email.trim()}
                    data-testid="approve-submit"
                    className="mt-5 w-full h-14 rounded-md bg-ink-900 hover:bg-ink-800 disabled:opacity-40 text-cream serif text-lg group">
              {submitLabel(busy, showAlaCarte, pickedCount, darkeBumped)}
            </Button>

            <p className="mt-3 text-[11px] text-ink-900/55 leading-relaxed text-center">
              Clicking this is the green-light to start the build. No contract until you walk the prototype and say yes.
            </p>
          </form>
          )}

          {/* Alt contact paths (non-legal-primary only) */}
          {!legalPrimary && (
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <a href="mailto:colindarke@complisun.com" className="border border-ink-900/10 rounded-md p-3 bg-white/55 hover:bg-white transition-colors group" data-testid="approve-email-link">
              <Mail className="h-4 w-4 text-ink-900/55 mx-auto mb-1.5 group-hover:text-sun-700" strokeWidth={1.5} />
              <div className="mono text-[9px] uppercase tracking-[0.18em] text-ink-900/55 group-hover:text-sun-700">Email</div>
              <div className="text-[11px] text-ink-900/65 leading-tight truncate">colindarke@complisun.com</div>
            </a>
            <a href="tel:+13136174473" className="border border-ink-900/10 rounded-md p-3 bg-white/55 hover:bg-white transition-colors group">
              <Phone className="h-4 w-4 text-ink-900/55 mx-auto mb-1.5 group-hover:text-sun-700" strokeWidth={1.5} />
              <div className="mono text-[9px] uppercase tracking-[0.18em] text-ink-900/55 group-hover:text-sun-700">Call</div>
              <div className="text-[11px] text-ink-900/65 leading-tight">313 · 617 · 4473</div>
            </a>
            <a href="https://complisunguardian.com" target="_blank" rel="noreferrer" className="border border-ink-900/10 rounded-md p-3 bg-white/55 hover:bg-white transition-colors group">
              <Calendar className="h-4 w-4 text-ink-900/55 mx-auto mb-1.5 group-hover:text-sun-700" strokeWidth={1.5} />
              <div className="mono text-[9px] uppercase tracking-[0.18em] text-ink-900/55 group-hover:text-sun-700">Try free</div>
              <div className="text-[11px] text-ink-900/65 leading-tight">60-day trial</div>
            </a>
          </div>
          )}

          {scope === "master" && (
            <div className="mt-3 mono text-[9px] uppercase tracking-[0.22em] text-ink-900/40 text-center">
              / master view · approval emails colindarke@complisun.com
            </div>
          )}
        </div>
      </div>

      {/* Corporate (1B) prospects — show complete pricing summary at the close */}
      {showCorpPricing && !done && (isSloan || (legalPrimary && (isSloan || isDykema))) && (
        <div className="mt-8" data-testid="sloan-darke-wrap-contact">
          <DarkeDiscountCallout
            slug={slug}
            question="Do you know Kimberly Darke?"
            hint="(say yes for a special rate)"
            unlockedHeadline="Darke discount unlocked"
            unlockedSub="25% off CompliSun Legal — locked in for the first 12 months."
            noResponseText="No worries — Founding Client pricing (20% off) still stands."
            testId="sloan-darke-contact"
          />
        </div>
      )}
      {showCorpPricing && !done && (() => {
        const corpDisc = darkeBumped ? 0.25 : 0.20;
        const corpLabel = Math.round(corpDisc * 100);
        const tier = (std) => Math.round(std * (1 - corpDisc));
        const legalCard = (
          <div className="border border-ember-500/40 rounded-md p-5 bg-gradient-to-br from-ember-500/10 to-transparent" key="legal">
            <div className="flex items-center justify-between mb-2">
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-ember-700">/ {legalPrimary ? "primary · " : ""}legal · CompliSun Legal PLC</div>
              <div className="mono text-[9px] uppercase tracking-[0.20em] bg-ember-500 text-cream rounded-full px-2 py-0.5">
                {darkeBumped ? "Family" : "Founding"} · {corpLabel}% · 12 mo
              </div>
            </div>
            <ul className="space-y-2.5 text-sm text-ink-900/85">
              <li className="flex items-baseline justify-between gap-2 border-b border-ink-900/8 pb-2">
                <span>Hotline</span>
                <span className="serif text-lg text-ink-900">
                  <span className="text-xs text-ink-900/45 line-through mr-1.5">$1,250</span>${tier(1250).toLocaleString()}<span className="text-xs text-ink-900/60">/mo</span>
                </span>
              </li>
              <li className="flex items-baseline justify-between gap-2 border-b border-ink-900/8 pb-2">
                <span>Counsel</span>
                <span className="serif text-lg text-ink-900">
                  <span className="text-xs text-ink-900/45 line-through mr-1.5">$4,500</span>${tier(4500).toLocaleString()}<span className="text-xs text-ink-900/60">/mo</span>
                </span>
              </li>
              <li className="flex items-baseline justify-between gap-2">
                <span>Fractional Team</span>
                <span className="serif text-lg text-ink-900">
                  <span className="text-xs text-ink-900/45 line-through mr-1.5">$9,500</span>${tier(9500).toLocaleString()}<span className="text-xs text-ink-900/60">/mo</span>
                </span>
              </li>
            </ul>
          </div>
        );
        const platformCard = (
          <div className={`border rounded-md p-5 ${legalPrimary ? "border-ink-900/15 bg-white/55" : "border-sun-500/40 bg-gradient-to-br from-sun-500/10 to-transparent"}`} key="platform">
            <div className="mono text-[10px] uppercase tracking-[0.22em] mb-2 flex items-center gap-2 flex-wrap">
              <span className={legalPrimary ? "text-ink-900/55" : "text-sun-700"}>/ {legalPrimary ? "secondary · " : ""}platform · CompliSun</span>
            </div>
            <ul className="space-y-2.5 text-sm text-ink-900/85">
              <li className="flex items-baseline justify-between gap-2 border-b border-ink-900/8 pb-2">
                <span>Platform only</span>
                <span className="serif text-lg text-ink-900">$1,500<span className="text-xs text-ink-900/60">/mo</span></span>
              </li>
              <li className="flex items-baseline justify-between gap-2">
                <span>Platform + Fractional Team</span>
                <span className="serif text-lg text-ink-900">$3,225<span className="text-xs text-ink-900/60">/mo</span></span>
              </li>
              <li className="text-xs text-ink-900/60 italic pt-1">+ $5,000 one-time installation (45-day rollout)</li>
            </ul>
          </div>
        );
        return (
          <div className={`${isSloan || legalPrimary ? "mt-4" : "mt-8"} grid grid-cols-1 md:grid-cols-2 gap-4`} data-testid="corp-pricing-summary">
            {legalPrimary ? [legalCard, platformCard] : [platformCard, legalCard]}
          </div>
        );
      })()}

      {/* Legal cross-sell banner — only for designated non-law-firm prospects */}
      {showLegalBanner && (
        <button
          type="button"
          onClick={() => goNext && goNext()}
          data-testid="legal-cross-sell-banner"
          className="mt-8 w-full group flex items-center justify-between gap-4 border border-sun-500/50 bg-gradient-to-r from-sun-500/10 via-ember-500/5 to-sun-500/10 rounded-md px-5 py-4 hover:border-sun-500 hover:shadow-sm transition-all text-left"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 rounded-full bg-sun-500/15 border border-sun-500/40 grid place-items-center flex-shrink-0">
              <Scale className="h-4 w-4 text-sun-700" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">/ bonus · for {prospect?.company_name || "you"}</div>
              <div className="serif text-base text-ink-900 leading-tight mt-0.5">
                Need legal help, too? Meet <em className="italic text-sun-700">CompliSun Legal PLC</em>.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/65 group-hover:text-sun-700 transition-colors flex-shrink-0">
            See the bonus slide
            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </button>
      )}
    </SlideFrame>
  );
}

function heroTitle(legalPrimary, prospect) {
  if (legalPrimary) return <>Engage <em className="italic text-sun-600">CompliSun Legal</em>.</>;
  if (prospect) return <>Build <em className="italic text-sun-600">{prospect.company_name}</em>'s prototype.</>;
  return <>Build <em className="italic text-sun-600">my prototype.</em></>;
}

function heroIntro(legalPrimary, lf, lfDiscFamily, darkeBumped) {
  if (legalPrimary) {
    return (
      <>Schedule a 30-minute legal intro with Colin. We'll walk your top three priorities — commercial, regulatory, IP, employment — and pick the tier that fits this quarter. Your discount{darkeBumped ? <strong className="text-ink-900"> (25%)</strong> : <strong className="text-ink-900"> (20%)</strong>} locks in when you book the call.</>
    );
  }
  if (lf) {
    return (
      <>We build a working prototype of the platform — configured to your firm, your matter types, your client base — in 45 days. {lfDiscFamily ? <>Family rate locks in <strong className="text-ink-900">25% off both the user fee and installation for the first 12 months</strong>.</> : <>Founding firms get <strong className="text-ink-900">20% off the user fee for the first 12 months and 20% off installation</strong>.</>}</>
    );
  }
  return (
    <>Clicking the button below is the green-light for us to start building your prototype. No contract yet. No money out the door. Just: <strong className="text-ink-900">"yes, build it and show me."</strong></>
  );
}

function submitLabel(busy, showAlaCarte, pickedCount, darkeBumped) {
  const pct = darkeBumped ? "25%" : "20%";
  if (busy) return <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending…</>;
  if (showAlaCarte && pickedCount > 0) {
    return <>Build my prototype · {pickedCount} module{pickedCount === 1 ? "" : "s"} · lock in {pct} <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-0.5 transition-transform" /></>;
  }
  return <>Build me a prototype · lock in the {pct} <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-0.5 transition-transform" /></>;
}
